import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./collection.css";
import { getProductImageUrl } from "../../../utils/imageUrl";

const ArrowIcon = ({ direction }) => (
	<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
		{direction === "left" ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
	</svg>
);
const ExternalIcon = () => (
	<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
);

function Collections({ data, latestYear = 2026 }) {
	const scrollContainerRef = useRef(null);
	const [showLeftArrow, setShowLeftArrow] = useState(false);
	const [showRightArrow, setShowRightArrow] = useState(false);

	const collections = useMemo(() => {
		if (!Array.isArray(data)) return [];
		return [...data]
			.filter((item) => item?.viewInfront === true && getProductImageUrl(item))
			.sort((a, b) => {
				const ay = Number(a?.year || 0);
				const by = Number(b?.year || 0);
				if (ay === latestYear && by !== latestYear) return -1;
				if (ay !== latestYear && by === latestYear) return 1;
				if (ay !== by) return by - ay;
				return String(a?.style || "").localeCompare(String(b?.style || ""));
			})
			.slice(0, 5);
	}, [data, latestYear]);

	const updateArrowVisibility = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;
		const { scrollLeft, scrollWidth, clientWidth } = container;
		setShowLeftArrow(scrollLeft > 8);
		setShowRightArrow(scrollWidth - clientWidth - scrollLeft > 8);
	}, []);

	useEffect(() => {
		updateArrowVisibility();
		window.addEventListener("resize", updateArrowVisibility, { passive: true });
		return () => window.removeEventListener("resize", updateArrowVisibility);
	}, [collections, updateArrowVisibility]);

	const scroll = useCallback((direction) => {
		scrollContainerRef.current?.scrollBy({ left: direction * 350, behavior: "smooth" });
	}, []);

	return (
		<section className="mb-28 px-4 md:px-12 lg:px-20" aria-labelledby="signature-title">
			<div className="text-center mb-16">
				<h2 id="signature-title" className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-4">Signature Collections</h2>
				<div className="w-24 h-0.5 bg-[#8a459f] mx-auto" aria-hidden="true" />
			</div>

			<div className="w-full flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
				<div className="lg:w-2/5 w-full">
					<div className="sticky top-8 space-y-8">
						<div>
							<h3 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-4 leading-tight">Premiere Gowns Collection</h3>
							<p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">Experience unparalleled elegance with our {latestYear} Premiere Collection. Each gown is meticulously crafted with premium fabrics and attention to detail.</p>
							<ul className="space-y-3 mb-8">
								<li className="flex items-center gap-3"><span className="w-2 h-2 bg-[#8a459f] rounded-full" aria-hidden="true" />Premium silk & satin fabrics</li>
								<li className="flex items-center gap-3"><span className="w-2 h-2 bg-[#8a459f] rounded-full" aria-hidden="true" />Hand-finished details</li>
							</ul>
						</div>
						<Link to={`/gowns/${latestYear}`} className="inline-flex items-center gap-3 px-8 py-4 bg-[#713580] text-white font-medium text-lg rounded-lg hover:bg-[#633072] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-purple-300 transition">
							Explore Collection <ExternalIcon />
						</Link>
					</div>
				</div>

				<div className="lg:w-3/5 w-full relative">
					<div className="relative">
						<div ref={scrollContainerRef} className="flex gap-6 lg:gap-8 overflow-x-auto scroll-smooth no-scrollbar pb-6" onScroll={updateArrowVisibility}>
							{collections.map((collection) => (
								<Link
									to={`/Gowns/id/${collection.style}`}
									state={{ product: collection }}
									key={collection._id || collection.style}
									className="flex-shrink-0 w-[280px] sm:w-[320px] lg:w-[380px] group focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#8a459f] rounded-2xl"
									aria-label={`View gown style ${collection.style}`}
								>
									<div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 bg-gray-100">
										<img src={getProductImageUrl(collection)} alt={`Risky gown style ${collection.style}`}  className="w-full h-[400px] lg:h-[480px] object-cover object-top transition-transform duration-300 group-hover:scale-105" />
										<div className="absolute inset-x-0 bottom-0 bg-black/65 text-white p-4 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
											<span className="font-medium">View Details</span>
										</div>
									</div>
									<div className="text-center px-2"><h4 className="font-serif text-xl text-gray-900 mb-2">{collection.style || collection.name}</h4></div>
								</Link>
							))}
						</div>

						{showLeftArrow && <button type="button" onClick={() => scroll(-1)} aria-label="Scroll collection left" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-gray-900 p-3 rounded-full shadow-lg w-12 h-12 flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-purple-300"><ArrowIcon direction="left" /></button>}
						{showRightArrow && <button type="button" onClick={() => scroll(1)} aria-label="Scroll collection right" className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-gray-900 p-3 rounded-full shadow-lg w-12 h-12 flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-purple-300"><ArrowIcon direction="right" /></button>}
					</div>
				</div>
			</div>
		</section>
	);
}

export default memo(Collections);
